/**
 * Reinigen Detail Page
 *
 * Form for cleaning (reinigen) process of wasserij items. Werkbank-render
 * (plan fase 2): foto bovenaan, feiten, ingeklapte leessecties, hoe-reinigen
 * als tegels, wasrapport, Opslaan vast onderin. Payload ongewijzigd.
 * Shows: object dimensions (read-only), voorinspectie data (read-only), existing wasrapport
 * Captures: cleaning method (hoe reinigen), additional wasrapport notes
 * Supports offline saving with sync queue.
 */

import { useState, useEffect, useMemo } from 'react';
import { RemoteCameraPanel } from '../components/RemoteCameraPanel';
import { useParams, Link, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { WbButton, WbChip, WbFactRow, WbFlag, WbState, WbTextarea, WbTile, StickyActionBar } from '../components/wb';
import { useHeader } from '../stores';
import { salesforceClient } from '../lib/salesforce/client';
import { isServerWeigering } from '../lib/sync/wasserij-wachtrij';
import { HOE_REINIGEN_OPTIONS, VOORINSPECTIE_OPTIONS } from '../lib/constants';
import { CameraCapture } from '../components/CameraCapture';
import { MediaStrip } from '../components/MediaStrip';
import {
  getCachedReinigenItems,
  queuePendingReinigen,
  removeCachedReinigenItem,
} from '../lib/db/database';
import type { ReinigenItem } from '../lib/salesforce/types';
import { BerichtenInline } from '../components/berichten/BerichtenInline';
import { KlachtBanner } from '../components/KlachtBanner';

export function ReinigenDetailPage() {
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useNavigate();

  // Data state
  const [item, setItem] = useState<ReinigenItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveFout, setSaveFout] = useState<string | null>(null);

  // Form state
  const [hoeReinigen, setHoeReinigen] = useState<string>('');
  const [wasrapport, setWasrapport] = useState('');
  const [showWasrapportInput, setShowWasrapportInput] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [mediaRefresh, setMediaRefresh] = useState(0); // MediaStrip herladen na capture
  const [voorinspectieOpen, setVoorinspectieOpen] = useState(true);
  const [wasrapportOpen, setWasrapportOpen] = useState(true);

  // Load item data
  useEffect(() => {
    async function loadItem() {
      if (!itemId) return;

      setIsLoading(true);
      setError(null);

      try {
        // Try API first
        const response = await salesforceClient.getReinigenItems();
        console.log('[ReinigenDetailPage] API response:', response);

        if (response.success && response.data?.items) {
          const foundItem = response.data.items.find((i) => i.itemId === itemId);

          if (foundItem) {
            setItem(foundItem);
            // Pre-fill hoeReinigen if already set
            if (foundItem.hoeReinigen) setHoeReinigen(foundItem.hoeReinigen);
          } else {
            setError('Item niet gevonden');
          }
        } else {
          throw new Error(response.message || 'Kon items niet ophalen');
        }
      } catch (err) {
        console.warn('[ReinigenDetailPage] API failed, trying cache:', err);

        // Fallback to cache
        try {
          const cached = await getCachedReinigenItems();

          if (cached.length > 0) {
            const foundItem = cached.find((c) => c.itemId === itemId);

            if (foundItem) {
              setItem({
                itemId: foundItem.itemId,
                itemName: foundItem.itemName,
                productName: foundItem.productName,
                werkOrderId: foundItem.werkOrderId,
                werkOrderName: foundItem.werkOrderName,
                companyCamProjectId: foundItem.companyCamProjectId,
                lengthCm: foundItem.lengthCm,
                widthCm: foundItem.widthCm,
                diameterCm: foundItem.diameterCm,
                surfaceM2: foundItem.surfaceM2,
                vorm: foundItem.vorm,
                voorinspectie: foundItem.voorinspectie,
                voorinspectieOpmerkingen: foundItem.voorinspectieOpmerkingen,
                wasrapport: foundItem.wasrapport,
                hoeReinigen: foundItem.hoeReinigen,
                secondaryItems: foundItem.secondaryItems || [],
              });
              if (foundItem.hoeReinigen) setHoeReinigen(foundItem.hoeReinigen);
            } else {
              setError('Item niet gevonden in cache');
            }
          } else {
            setError('Geen verbinding en geen gecachte data');
          }
        } catch (cacheErr) {
          console.error('[ReinigenDetailPage] Cache also failed:', cacheErr);
          setError('Kon item niet laden');
        }
      } finally {
        setIsLoading(false);
      }
    }

    loadItem();
  }, [itemId]);

  // Parse voorinspectie string into badge array
  const voorinspectieBadges = useMemo(() => {
    if (!item?.voorinspectie) return [];

    const values = item.voorinspectie.split(';').filter(v => v.trim());

    return values.map(value => {
      // Try to find matching label from VOORINSPECTIE_OPTIONS
      const option = VOORINSPECTIE_OPTIONS.find(opt => opt.value === value);
      return {
        value,
        label: option?.label || value, // Fallback to raw value if not found
      };
    });
  }, [item?.voorinspectie]);

  // Save reinigen data
  const handleSave = async () => {
    if (!item || !itemId) return;

    // Validation: hoeReinigen is required
    if (!hoeReinigen) {
      setError('Selecteer een reinigingsmethode');
      return;
    }

    setIsSaving(true);
    setError(null);
    setSaveFout(null);

    const data = {
      hoeReinigen,
      wasrapport: wasrapport || undefined,
      markComplete: true, // Status wordt 'Drogen'
    };

    console.log('[ReinigenDetailPage] Saving:', { itemId, data });

    try {
      // Try API first
      const response = await salesforceClient.saveReinigen(itemId, data);
      console.log('[ReinigenDetailPage] Save response:', response);

      if (response.success) {
        // Remove from cache after successful save
        await removeCachedReinigenItem(itemId);

        // Haptic feedback on success
        if (navigator.vibrate) {
          navigator.vibrate(50);
        }

        navigate('/reinigen');
      } else {
        throw new Error(response.message || 'Opslaan mislukt');
      }
    } catch (err) {
      // Weigering van de server (bv. RISICO_OPEN): tonen en blijven staan.
      // Tot 03-11 ging die stil de nooit verstuurde wachtrij in (#144/#122).
      if (isServerWeigering(err)) {
        setSaveFout(err.message);
        if (navigator.vibrate) navigator.vibrate([100, 50, 100, 50, 100]);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      console.warn('[ReinigenDetailPage] API save failed, queuing offline:', err);

      // Queue for offline sync (lib/sync/wasserij-wachtrij.ts verstuurt)
      await queuePendingReinigen({
        itemId,
        itemName: item.itemName,
        hoeReinigen,
        wasrapport: wasrapport || undefined,
        markComplete: true,
      });

      // Remove from cache (it's "done" locally)
      await removeCachedReinigenItem(itemId);

      // Still navigate (optimistic UI)
      if (navigator.vibrate) {
        navigator.vibrate([50, 30, 50]); // Different pattern for offline
      }

      navigate('/reinigen');
    } finally {
      setIsSaving(false);
    }
  };

  useHeader(item ? item.itemName : 'Reinigen');

  if (isLoading) {
    return <WbState variant="laden" titel="Item laden…" className="text-center min-h-[60vh] flex items-center justify-center" />;
  }

  if (!item) {
    return (
      <div className="space-y-3">
        <Link to="/reinigen" className="wb-focus inline-flex items-center min-h-tap font-semibold underline underline-offset-4 text-wb-inkt">
          ‹ Terug naar lijst
        </Link>
        <WbFlag kind="geld" title="Kon item niet laden">{error || 'Item niet gevonden'}</WbFlag>
      </div>
    );
  }

  const isFormValid = hoeReinigen.trim().length > 0;
  const maat = item.lengthCm && item.widthCm
    ? `${item.lengthCm} × ${item.widthCm}`
    : item.diameterCm
      ? `Ø ${item.diameterCm}`
      : item.surfaceM2
        ? `${item.surfaceM2.toFixed(2)} m²`
        : '–';
  const pakket = item.secondaryItems?.find((s) => s.category === 'Package')?.productName;
  const extras = (item.secondaryItems ?? []).filter((s) => s.category !== 'Package');
  const heeftVoorinspectie = voorinspectieBadges.length > 0 || !!item.voorinspectieOpmerkingen;

  return (
    <div className="space-y-5 pb-[140px]">
      {saveFout && <WbFlag kind="geld" title="Niet opgeslagen">{saveFout}</WbFlag>}
      {/* Terug + WI groot */}
      <Link to="/reinigen" className="wb-focus inline-flex items-center gap-1 min-h-tap font-mono text-xl font-semibold text-wb-inkt">
        <span aria-hidden>‹</span> {item.itemName}
      </Link>

      {item.isKlacht && <KlachtBanner reden={item.klachtReden} />}
      {error && <WbFlag kind="opmerking" title="Let op">{error}</WbFlag>}

      {/* Foto bovenaan: altijd de eigen camera (CompanyCam is uit de wasserij weg) */}
      <Sectie titel="Foto">
        <WbButton variant="secondary" onClick={() => setShowCamera(true)}>
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Foto maken
        </WbButton>
        <MediaStrip wiSfId={item.itemId} refreshKey={mediaRefresh} />
      </Sectie>

      {/* Feiten */}
      <RemoteCameraPanel wiSfId={item.itemId} wiName={item.itemName} />
      <WbFactRow
        facts={[
          { label: 'Maat', value: maat },
          { label: 'Pakket', value: pakket ?? item.productName, tekst: true },
          { label: 'Order', value: item.werkOrderName },
        ]}
      />
      {extras.length > 0 && (
        <div className="flex flex-wrap gap-1 -mt-3">
          {extras.map((e) => <WbChip key={e.itemId} kind="neutraal">{e.productName}</WbChip>)}
        </div>
      )}

      {/* Berichten */}
      <BerichtenInline
        scope="wi"
        werkOrderId={item.werkOrderId}
        werkOrderName={item.werkOrderName}
        wasserijItemId={item.itemId}
        wasserijItemName={item.itemName}
      />

      {/* Voorinspectie en bestaand wasrapport: lezen, standaard open (Albert 21-10), dichtklappen kan */}
      {heeftVoorinspectie && (
        <Inklap titel="Voorinspectie" teller={voorinspectieBadges.length} open={voorinspectieOpen} onToggle={() => setVoorinspectieOpen((v) => !v)}>
          {voorinspectieBadges.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {voorinspectieBadges.map((b) => <WbChip key={b.value} kind="neutraal">{b.label}</WbChip>)}
            </div>
          )}
          {item.voorinspectieOpmerkingen && <WbFlag kind="info" title="Opmerkingen voorinspectie">{item.voorinspectieOpmerkingen}</WbFlag>}
        </Inklap>
      )}
      {item.wasrapport && (
        <Inklap titel="Bestaand wasrapport" open={wasrapportOpen} onToggle={() => setWasrapportOpen((v) => !v)}>
          <WbFlag kind="info">{item.wasrapport}</WbFlag>
        </Inklap>
      )}

      {/* Hoe reinigen: één keuze, volgorde op meting */}
      <Sectie titel="Hoe reinigen">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
          {HOE_REINIGEN_OPTIONS.map((option) => (
            <WbTile
              key={option.value}
              rol="radio"
              selected={hoeReinigen === option.value}
              onToggle={() => setHoeReinigen(option.value)}
            >
              {option.label}
            </WbTile>
          ))}
        </div>
      </Sectie>

      {/* Opmerkingen ingeklapt */}
      <Sectie titel="Wasrapport">
        {!showWasrapportInput && !wasrapport ? (
          <WbButton variant="link" onClick={() => setShowWasrapportInput(true)}>Opmerking toevoegen ›</WbButton>
        ) : (
          <WbTextarea
            label="Intern (wasrapport)"
            value={wasrapport}
            onChange={(e) => setWasrapport(e.target.value)}
            placeholder="Eventuele bijzonderheden…"
            autoFocus={showWasrapportInput}
          />
        )}
      </Sectie>

      <StickyActionBar>
        <WbButton size="lg" onClick={() => handleSave()} isLoading={isSaving} disabled={!isFormValid}>
          {isFormValid ? 'Opslaan' : 'Kies hoe reinigen'}
        </WbButton>
      </StickyActionBar>

      {showCamera && (
        <CameraCapture
          fase="reinigen"
          doel={{ wiSfId: item.itemId }}
          subtitle={item.itemName}
          captureStatus="Klaar voor Reinigen"
          onClose={(aantal) => {
            setShowCamera(false);
            if (aantal > 0) setMediaRefresh((k) => k + 1);
          }}
        />
      )}
    </div>
  );
}

function Sectie({ titel, children }: { titel: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="font-wb-heading font-bold text-xs uppercase tracking-wide text-wb-inkt2">{titel}</h2>
      {children}
    </section>
  );
}

/** Ingeklapte leessectie: kop met chevron, inhoud eronder. */
function Inklap({ titel, teller, open, onToggle, children }: { titel: string; teller?: number; open: boolean; onToggle: () => void; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <button type="button" onClick={onToggle} aria-expanded={open} className="wb-focus w-full min-h-tap flex items-center justify-between gap-2 px-3 bg-wb-vlak border-wb border-wb-rand rounded-wb text-left">
        <span className="font-wb-heading font-bold text-xs uppercase tracking-wide text-wb-inkt2">
          {titel}{teller ? <span className="font-mono ml-1.5">{teller}</span> : null}
        </span>
        <svg className={clsx('w-5 h-5 text-wb-inkt transition-transform', open && 'rotate-180')} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <div className="space-y-2">{children}</div>}
    </section>
  );
}
